<template>
  <div>
    <dialog-confirm ref="remove-api-key">
      <div class="text-subtitle-1">
        {{ $t('customer_apikey_ui.are_you_sure_you_want_to_revoke_this_api_key') }}
      </div>
    </dialog-confirm>
    <v-dialog v-model="newApiKeyDialog" width="auto" location="top">
      <v-sheet outlined rounded class="px-4 py-2">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="newApiKeyName"
                  :label="$tc('customer_apikey_ui.api_key_name')"/>
              </v-col>
              <v-col>
                {{ $t('customer_apikey_ui.api_key_name_help') }}
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn
              outlined
              color="error"
              @click="newApiKeyDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              outlimed
              @click="addApiKey(newApiKeyName)">
              <v-icon class="ml-n2">mdi-content-save</v-icon>
              {{ $t('customer_apikey_ui.add_new_api_key') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-sheet>

    </v-dialog>
    <v-dialog v-model="createdApiDialog" location="top" width="auto">
      <v-card>
        <v-card-title>
          {{ $t('customer_apikey_ui.api_key_created') }}
        </v-card-title>
        <v-card-text>
          <div v-if="addedApiKey">
            <p>{{ $t('customer_apikey_ui.please_make_a_note_secret') }}</p>
            <v-row>
              <v-col sm="2" class="text-sm-right">{{ $tc('api_key') }}</v-col>
              <v-col sm="6">{{ addedApiKey.key }}</v-col>
            </v-row>
            <v-row>
              <v-col sm="2" class="text-sm-right">{{ $t('secret') }}</v-col>
              <v-col sm="6">{{ addedApiKey.secret }}</v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <div class="d-flex justify-space-between align-center">
      <h4> {{ $tc('api_key', 2) }} </h4>
      <v-btn
        color="primary"
        @click="showAddModal()"
        outlined>
        <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
        {{ $t('customer_apikey_ui.add_api_key') }}
      </v-btn>
    </div>
    <div>
      <v-card v-for="(apiKey, index) of apiKeys" :key="index" class="ma-4" elevation="2">
        <v-card-title>
          {{ apiKey.name }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="2" class="text-sm-right">{{ $tc('api_key') }}</v-col>
            <v-col sm="6">{{ apiKey.key }}</v-col>
          </v-row>
          <v-row>
            <v-col sm="2" class="text-sm-right">{{ $t('created') }}</v-col>
            <v-col sm="6">
              <multi-time-display :time="apiKey.created"/>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row>
            <v-col class="text-right">
              <v-btn outlined type="submit" class="my-2" color="danger" @click="deleteApiKey(apiKey)">
                <v-icon>mdi-delete</v-icon>
                {{ $t('customer_apikey_ui.revoke_api_key') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
      <p v-if="isEmpty(apiKeys)" class="font-italic">
        {{ $t('customer_apikey_ui.no_api_keys_configured_for_this_customer') }}</p>
    </div>
  </div>
</template>

<script>
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import DialogConfirm from '@/components/shared/DialogConfirm'
import { isEmpty } from 'lodash'

export default {
  name: 'CustomerApiKeys',
  mixins: [ApiUtilitiesMixin],
  components: {
    MultiTimeDisplay,
    DialogConfirm
  },
  props: {
    customer: Object
  },
  data () {
    return {
      isEmpty: isEmpty,
      apiKeys: [],
      newApiKeyName: null,
      newApiKeyDialog: false,
      createdApiDialog: false,
      isAdding: false,
      addedApiKey: null
    }
  },
  methods: {
    /**
     * Retrieve the API keys for this customer from the API endpoint
     */
    fetchApiKeys () {
      const requestParams = {
        customer_id: this.customer.id,
        // fields: 'id,name,customers.name',
        'per-page': 0
      }
      this.$orm('CustomerApikey').findAll(requestParams)
        .then((resp) => {
          this.apiKeys = resp.getItems()
        })
        .catch((error) => {
          if (error) {
            this.renderErrorFlashBanner(error)
            throw (error)
          }
        })
    },
    /**
     * Display the add API key modal dialog
     */
    showAddModal () {
      this.addedApiKey = null
      this.newApiKeyName = null
      this.newApiKeyDialog = true
    },
    /**
     * Push a new API key for this customer to the CustomerApikey endpoint
     * @param name
     */
    addApiKey (name) {
      this.isAdding = true

      const newApiKey = this.$orm('CustomerApikey').createRecord({
        customer_id: this.customer.id,
        name: name
      })

      this.$orm('CustomerApikey').createOrUpdate(newApiKey)
        .then((apikey) => {
          this.addedApiKey = apikey
          this.newApiKeyDialog = false
          this.createdApiDialog = true
          this.fetchApiKeys()
        })
        .catch((error) => {
          if (error) {
            this.renderErrorFlashBanner(error)
            throw (error)
          }
        })
        .finally(() => {
          this.isAdding = false
        })
    },
    /**
     * Send a request to delete the provided API key
     * @param apiKey
     * @returns {Promise<void>}
     */
    async deleteApiKey (apiKey) {
      if (await this.$refs['remove-api-key'].confirm()) {
        apiKey.destroy()
          .then(resp => {
            this.fetchApiKeys()
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              `${this.$i18n.tc('api_key')} ${this.$i18n.t('generic_delete_success')}`
            )
          })
          .catch(error => {
            if (error) {
              this.renderErrorModal(error)
              throw (error)
            }
          })
      }
    }
  },
  mounted () {
    this.fetchApiKeys()
  }
}
</script>

<style scoped>

</style>
